import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { APP_CONFIG } from 'app/app.config';
import {
    CreateCustomerRequest,
    LoginRequest,
    RefreshTokenRequest,
    UpdateCustomerRequest,
    DeleteCustomerRequest,
    DeleteUserRequest,
    CreateUserRequest,
    UpdateUserRequest,
    DeleteAgreementRequest,
    CreateAgreementRequest,
    UpdateAgreementRequest,
    PeriodRenewalsRequest,
    AgreementNoteRemindersRequest,
    PaymentRequest,
    ReminderRequest,
    UpdateCounterpartRequest,
    CreateCounterpartRequest,
    FilterRequest,
    MoveResponsibleUserRequest,
    GenerateMetaDataRequest,
    CreateListTypeRequest,
    SubscriptionRequest,
    RenegotiateAgreementRequest,
    QueueAIRequest,
    CounterpartEsgReportRequest
} from './requests';
import { DeleteEndPoints, GetEndPoints, PostEndPoints, PutEndPoints } from './endpoints';
import {
    CreateUserResponse,
    GetCustomersResponse,
    GetUsersResponse,
    CustomerResponse,
    UpdateUserResponse,
    GetAgreementResponse,
    GetCounterpartsResponse,
    CounterpartResponse,
    GetInboxesResponse,
    GetAgreementsResponse,
    GetNameValueResponse,
    GetCalendarInfoResponse,
    TemplateResponse,
    GetActivitiesResponse,
    GetAgreementNoteRemindersResponse,
    DashboardResponse,
    SubscriptionResponse,
    MetaDataResponse,
    GetInventoriesResponse,
    GetUserResponse,
    UserProfileResponse,
    QueueAIResponse
} from './responses';
import { AgreementFilesRequest } from './requests/agreement-files-request.type';
import { Predicate } from 'app/enums';
import { AgreementInventory, AgreementRealEstate, AgreementRental } from 'app/types';
import { GetListTypesResponse } from './responses/get-listtype-response.type';
@Injectable({
    providedIn: 'root'
})
export class ApiClientService {
    private readonly httpClient = inject(HttpClient);

    public readonly apiBaseUrl = APP_CONFIG.apiBaseUrl;

    public login$(loginRequest: LoginRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.Login}`, loginRequest);
    }

    public refreshToken$(refreshTokenRequest: RefreshTokenRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.RefershToken}`, refreshTokenRequest);
    }

    public sendPasswordResetCode$(value: string) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.SendPasswordResetCode}`, { email: value });
    }

    public resetPassword$(arg0: { email: string; code: string; newPassword: string }) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.ResetPassword}`, arg0);
    }

    public getCustomers$() {
        return this.httpClient.get<GetCustomersResponse>(`${this.apiBaseUrl}${GetEndPoints.Customers}`);
    }

    public getCustomer$(customerId: string) {
        return this.httpClient.get<CustomerResponse>(
            `${this.apiBaseUrl}${GetEndPoints.CustomerId.replace('{customer-id}', customerId)}`
        );
    }

    public createCustomer$(request: CreateCustomerRequest) {
        return this.httpClient.post<CustomerResponse>(`${this.apiBaseUrl}${PostEndPoints.Customers}`, request);
    }

    public updateCustomer$(request: UpdateCustomerRequest) {
        return this.httpClient.put<CustomerResponse>(
            `${this.apiBaseUrl}${PutEndPoints.Customer.replace('{customer-id}', request.id)}`,
            request
        );
    }

    public deleteCustomer$(request: DeleteCustomerRequest) {
        return this.httpClient.delete<DeleteCustomerRequest>(
            `${this.apiBaseUrl}${DeleteEndPoints.Customer.replace('{customer-id}', request.customerId)}`
        );
    }

    public getCounterpartById$(id: string) {
        return this.httpClient.get<CounterpartResponse>(
            `${this.apiBaseUrl}${GetEndPoints.CounterpartId.replace('{counterpart-id}', id)}`
        );
    }

    public getCounterparts$() {
        return this.httpClient.get<GetCounterpartsResponse>(`${this.apiBaseUrl}${GetEndPoints.Counterpart}`);
    }

    public createCounterpart$(request: CreateCounterpartRequest) {
        return this.httpClient.post<CounterpartResponse>(`${this.apiBaseUrl}${PostEndPoints.Counterpart}`, request);
    }

    public updateCounterpart$(request: UpdateCounterpartRequest) {
        return this.httpClient.put<CounterpartResponse>(
            `${this.apiBaseUrl}${PutEndPoints.Counterpart.replace('{counterpart-id}', request.id)}`,
            request
        );
    }

    public deleteCounterpart$(id: string) {
        return this.httpClient.delete<DeleteCustomerRequest>(
            `${this.apiBaseUrl}${DeleteEndPoints.Counterpart.replace('{counterpart-id}', id)}`
        );
    }

    public createUser$(request: CreateUserRequest) {
        return this.httpClient.post<CreateUserResponse>(`${this.apiBaseUrl}${PostEndPoints.User}`, request);
    }

    public getUsers$() {
        return this.httpClient.get<GetUsersResponse>(`${this.apiBaseUrl}${GetEndPoints.Users}`);
    }

    public getUser$(id: string) {
        return this.httpClient.get<GetUserResponse>(
            `${this.apiBaseUrl}${GetEndPoints.UserId.replace('{user-id}', id)}`
        );
    }

    public getUserProfile$(id: string) {
        return this.httpClient.get<UserProfileResponse>(
            `${this.apiBaseUrl}${GetEndPoints.UserProfileId.replace('{user-id}', id)}`
        );
    }

    public updateUser$(request: UpdateUserRequest) {
        return this.httpClient.put<UpdateUserResponse>(
            `${this.apiBaseUrl}${PutEndPoints.User.replace('{user-id}', request.userId)}`,
            request
        );
    }

    public deleteUser$(request: DeleteUserRequest) {
        return this.httpClient.delete<DeleteUserRequest>(
            `${this.apiBaseUrl}${DeleteEndPoints.User.replace('{user-id}', request.userId)}`
        );
    }

    public createAgreement$(request: CreateAgreementRequest) {
        return this.httpClient.post<GetAgreementResponse>(`${this.apiBaseUrl}${PostEndPoints.Agreements}`, request);
    }

    public updateAgreement$(agreementId: string, request: UpdateAgreementRequest) {
        return this.httpClient.put<GetAgreementResponse>(
            `${this.apiBaseUrl}${PutEndPoints.Agreements.replace('{agreement-id}', agreementId)}`,
            request
        );
    }

    public getAgreements$(predicates: Predicate<boolean | string>[]) {
        return this.httpClient.get<GetAgreementsResponse>(
            `${this.apiBaseUrl}${GetEndPoints.Agreements}?predicates=${JSON.stringify(predicates)}`
        );
    }

    public getInventories$(predicates: Predicate<string | boolean>[]) {
        return this.httpClient.get<GetInventoriesResponse>(
            `${this.apiBaseUrl}${GetEndPoints.Inventories}?predicates=${JSON.stringify(predicates)}`
        );
    }

    public getAgreement$(agreementId: string) {
        return this.httpClient.get<GetAgreementResponse>(
            `${this.apiBaseUrl}${GetEndPoints.AgreementId.replace('{agreement-id}', agreementId)}`
        );
    }

    // public getAgreementActivities$(agreementId: string, filter: FilterRequest) {
    //     return this.httpClient.get<GetActivitiesResponse>(
    //         `${this.apiBaseUrl}${GetEndPoints.AgreementActivities.replace('{agreement-id}', agreementId)}?filter=${JSON.stringify(filter)}`
    //     );
    // }

    public getAgreementNoteReminders$(predicates: Predicate<boolean | string>[]) {
        return this.httpClient.get<GetAgreementNoteRemindersResponse>(
            `${this.apiBaseUrl}${GetEndPoints.AgreementNoteReminders}?predicates=${JSON.stringify(predicates)}`
        );
    }

    public getAllTypeListItems$(predicates: Predicate<string | boolean>[]) {
        return this.httpClient.get<GetListTypesResponse>(
            `${this.apiBaseUrl}${GetEndPoints.AllListTypeItem}?predicates=${JSON.stringify(predicates)}`
        );
    }

    public getTypeListItems$(type: string) {
        return this.httpClient.get<GetNameValueResponse>(
            `${this.apiBaseUrl}${GetEndPoints.ListTypeItem.replace('{type}', type)}`
        );
    }

    public postListTypeItem$(type: string, request: CreateListTypeRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.ListTypeItem.replace('{type}', type)}`, request);
    }

    public deleteListType$(type: string, request: CreateListTypeRequest) {
        return this.httpClient.delete(`${this.apiBaseUrl}${DeleteEndPoints.ListType.replace('{type}', type)}`, {
            body: request
        });
    }

    public getAgreementTypes$() {
        return this.httpClient.get<GetNameValueResponse>(`${this.apiBaseUrl}${GetEndPoints.AgreementTypes}`);
    }

    public getAgreementTemplateByType$(typeId: number) {
        return this.httpClient.get<TemplateResponse>(
            `${this.apiBaseUrl}${GetEndPoints.TemplateByType.replace('{type-id}', typeId.toString())}`
        );
    }

    public getCommitmentPeriodTypes$() {
        return this.httpClient.get<GetNameValueResponse>(`${this.apiBaseUrl}${GetEndPoints.CommitmentPeriodTypes}`);
    }

    public getCalendarInfo$() {
        return this.httpClient.get<GetCalendarInfoResponse>(`${this.apiBaseUrl}${GetEndPoints.AgreementCalendarInfo}`);
    }

    public getDashboard$() {
        return this.httpClient.get<DashboardResponse>(`${this.apiBaseUrl}${GetEndPoints.Dashboard}`);
    }

    public getActivities$(filter: FilterRequest) {
        return this.httpClient.get<GetActivitiesResponse>(
            `${this.apiBaseUrl}${GetEndPoints.Activities}?predicates=${JSON.stringify(filter)}`
        );
    }

    public deleteAgreements$(request: DeleteAgreementRequest) {
        return this.httpClient.delete<DeleteAgreementRequest>(`${this.apiBaseUrl}${DeleteEndPoints.Agreements}`, {
            body: request
        });
    }

    public putAgreementPeriodsRenewals$(agreementId: string, request: PeriodRenewalsRequest) {
        return this.httpClient.put(
            `${this.apiBaseUrl}${PutEndPoints.AgreementPeriods.replace('{agreement-id}', agreementId)}`,
            request
        );
    }

    public putAgreementReminder$(agreementId: string, request: ReminderRequest) {
        return this.httpClient.put(
            `${this.apiBaseUrl}${PutEndPoints.AgreementReminder.replace('{agreement-id}', agreementId)}`,
            request
        );
    }

    public putAgreementPayment$(agreementId: string, request: PaymentRequest) {
        return this.httpClient.put(
            `${this.apiBaseUrl}${PutEndPoints.AgreementPayment.replace('{agreement-id}', agreementId)}`,
            request
        );
    }

    public putAgreementRealEstate$(agreementId: string, request: AgreementRealEstate) {
        return this.httpClient.put(
            `${this.apiBaseUrl}${PutEndPoints.AgreementRealEstate.replace('{agreement-id}', agreementId)}`,
            request
        );
    }

    public putAgreementRental$(agreementId: string, request: AgreementRental) {
        return this.httpClient.put(
            `${this.apiBaseUrl}${PutEndPoints.AgreementRental.replace('{agreement-id}', agreementId)}`,
            request
        );
    }

    public putAgreementInventory$(agreementId: string, request: AgreementInventory) {
        return this.httpClient.put(
            `${this.apiBaseUrl}${PutEndPoints.AgreementInventory.replace('{agreement-id}', agreementId)}`,
            request
        );
    }

    public putAgreementNoteReminders$(agreementId: string, request: AgreementNoteRemindersRequest) {
        return this.httpClient.put(
            `${this.apiBaseUrl}${PutEndPoints.AgreementNoteReminders.replace('{agreement-id}', agreementId)}`,
            request
        );
    }

    public putAgreementFiles$(agreementId: string, request: AgreementFilesRequest) {
        return this.httpClient.put(
            `${this.apiBaseUrl}${PutEndPoints.AgreementFiles.replace('{agreement-id}', agreementId)}`,
            request
        );
    }

    public getInboxItems$() {
        return this.httpClient.get<GetInboxesResponse>(`${this.apiBaseUrl}${GetEndPoints.Inbox}`);
    }

    public deleteInboxItem$(id: string) {
        return this.httpClient.delete(`${this.apiBaseUrl}${DeleteEndPoints.Inbox.replace('{inbox-id}', id)}`);
    }

    public moveResponsible$(request: MoveResponsibleUserRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.MoveResponsible}`, request);
    }

    public renegotiateAgreement$(request: RenegotiateAgreementRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.RenegotiateAgreement}`, request);
    }

    public getSubscription$() {
        return this.httpClient.get<SubscriptionResponse>(`${this.apiBaseUrl}${GetEndPoints.Subscription}`);
    }

    public updateSubscription(request: SubscriptionRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.Subscription}`, request);
    }

    // public generateWriteSas(request: StoredDataSasWriteRequest) {
    //     return this.httpClient.post<StoredDataSasWriteResponse>(
    //         `${this.apiBaseUrl}${PostEndPoints.GenerateWriteSas}`,
    //         request
    //     );
    // }

    public upload(file: File) {
        return this.httpClient.post<string>(`${this.apiBaseUrl}${PostEndPoints.Upload}`, file);
    }

    public download(agreementId: string) {
        return this.httpClient.get(
            `${this.apiBaseUrl}${GetEndPoints.Download.replace('{agreement-id}', agreementId)}`,
            {
                responseType: 'arraybuffer' // Specify that the response should be treated as a blob (binary data)
            }
        );
    }

    // public downloadTempFile(path: string) {
    //     const tmp = encodeURIComponent(path); //encodeURIComponent(JSON.stringify({ path: path }));
    //     return this.httpClient.get(`${this.apiBaseUrl}${GetEndPoints.DownloadTempFile.replace('{temp-path}', tmp)}`, {
    //         responseType: 'arraybuffer' // Specify that the response should be treated as a blob (binary data)
    //     });
    // }

    public downloadTempFile(path: string) {
        const request = { path: path };
        return this.httpClient.get(
            `${this.apiBaseUrl}${GetEndPoints.DownloadTempFile}?request=${JSON.stringify(request)}`,
            {
                responseType: 'arraybuffer' // Specify that the response should be treated as a blob (binary data)
            }
        );
    }
    // public downloadTempFile(path: string) {
    //     return this.httpClient.get(`${this.apiBaseUrl}${GetEndPoints.DownloadTempFile.replace('{temp-path}', path)}`, {
    //         responseType: 'arraybuffer' // Specify that the response should be treated as a blob (binary data)
    //     });
    // }

    public generateMetaData$(request: GenerateMetaDataRequest) {
        return this.httpClient.post<MetaDataResponse>(`${this.apiBaseUrl}${PostEndPoints.GenerateMetaData}`, request);
    }

    public getMetaData$(metadataId: string) {
        return this.httpClient.get<MetaDataResponse>(
            `${this.apiBaseUrl}${GetEndPoints.GenerateMetaData.replace('{metadata-id}', metadataId)}`
        );
    }

    public getAIGenerate$(id: string) {
        return this.httpClient.get<QueueAIResponse>(`${this.apiBaseUrl}${GetEndPoints.AIGenerate.replace('{id}', id)}`);
    }

    public postAIGenerate$(request: QueueAIRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.AIGenerate}`, request);
    }

    public getCounterpartEsgReport$(counterpartId: string) {
        return this.httpClient.get<string>(
            `${this.apiBaseUrl}${GetEndPoints.EsgReport.replace('{id}', counterpartId)}`
        );
    }

    public postCounterpartEsgReport$(counterpartId: string, request: CounterpartEsgReportRequest) {
        return this.httpClient.post(
            `${this.apiBaseUrl}${PostEndPoints.EsgReport.replace('{id}', counterpartId)}`,
            request
        );
    }
}
